import { confirmationItemsEn } from '@/entities/confirmationItems';
import type ja from './ja';

const en = {
    // サマータイム期間（2026/8/24-9/30）終了後は手動で取り下げる
    // 範囲を示す記号は文字化けを避けるため半角ハイフン（U+002D）を使用する
    summerTimeNotice: {
        delivery:
            '*Bags dropped off in the evening leave on our final run without waiting, so even an 18:00 drop-off arrives by <strong>around 22:00 on the same day</strong>.',
    },
    guide: {
        title: 'Registration',
        newApplication: {
            sectionTitle: 'First time applying',
            hotel: 'Hotel you plan to stay at',
            authCode: 'Hotel Access Code',
            authCodeHint:
                "⚠️ You'll find the code in your hotel's email or on the hotel's website. If you can't find it, please ask your hotel or Air Luggage on WhatsApp or LINE.",
            selectedHotelAddressLabel: 'Address of the selected hotel',
            selectedHotelAddressHint:
                'Please check that this address matches your hotel booking confirmation. Some of our hotels have similar names.',
            submit: 'Apply',
            error: 'Failed to verify Hotel Access Code',
        },
        existingApplication: {
            sectionTitle: 'Already applied',
            email: 'Email address',
            userName: 'Applicant Name',
            submit: 'CHECK STATUS',
            error: 'Application not found',
        },
        validation: {
            hotelRequired: 'Please select a hotel',
            authCodeRequired: 'Please enter the Hotel Access Code',
            authCodeLength: 'Hotel Access Code must be 6 digits',
            emailRequired: 'Please enter your email address',
            emailInvalid: 'Please enter a valid email address',
            userNameRequired: 'Please enter applicant name',
        },
    },
    input: {
        title: 'Delivery Booking',
        sections: {
            customerInfo: 'Customer Information',
            hotelInfo: 'Hotel Information',
            airportInfo: 'Airport in Japan',
            pickupCounter: 'Drop-off Counter',
            luggageInfo: 'Luggage Information',
            luggagePhoto: 'Luggage Photo',
            termsOfService: 'Terms of Service',
        },
        customerInfo: {
            reservationHolderName: '🔑 Hotel Reservation Name',
            reservationHolderNameHint: '<strong>Exactly as it appears on your hotel confirmation.</strong>',
            sameAsGuest: 'Same as guest',
            userName: 'Applicant Name (Person dropping off bags)',
            userNameHint: '⚠️ This person must be present at the counter to drop off the luggage.',
            relationshipWithGuest: 'Relationship with Guest',
            selectPlaceholder: 'Please select',
            email: 'Email Address',
            emailHint:
                "Enter the email of the person dropping off the bags. We'll send the day-before reminder and arrival notice here. Travel agents and tour guides: please enter each guest's own email.",
            phoneNumber: 'Phone Number',
            phoneNumberPlaceholder: 'Ex: +81 90-0000-0000',
            phoneNumberHint:
                'Please enter your country code and phone number.<br />If you have a phone number that can be reached during your stay in Japan, please enter it.',
            country: 'Country',
        },
        hotelInfo: {
            hotelName: 'Hotel Name',
            hotelAddress: 'Hotel Address',
            checkInDate: 'Check-in Date <strong>(JST)</strong>',
            checkOutDate: 'Check-out Date <strong>(JST)</strong>',
        },
        airportInfo: {
            airportCounter: 'Airport / Reception Counter',
            arrivalDateTime: 'Arrival/Stay Dates <strong>(JST, GMT+9)</strong>',
            flightNumber: 'Flight Number (Optional)',
        },
        pickupCounter: {
            arrivalDateTime: 'Arrival/Stay Dates <strong>(JST, GMT+9)</strong>',
            businessHours:
                '⚠️ Counter hours: <strong>{startTime}–{endTime}</strong> (JST). Strictly no drop-offs after {endTime}.',
        },
        estimatedDelivery: {
            label: 'Estimated Hotel Arrival',
            message: 'Delivery is scheduled to take approximately {hours} to {hoursUpper} hours.',
        },
        luggageInfo: {
            quantity: 'Estimated Number of Bags',
            hint: "An estimate is fine. It's okay if <strong>the final count is different</strong>; we will verify it at the counter.",
        },
        luggagePhoto: {
            description: 'Luggage Photo (up to 3)',
            addButton: 'Add Photo',
            count: '{current}/{max} photos',
            maxReached: 'Maximum of 3 photos allowed',
        },
        termsOfService: {
            agreeTerms: 'I agree to the Terms of Service and have read the Privacy Policy',
            variousLanguagesLink: 'Terms of Use in Various Languages',
        },
        submit: 'Proceed to Confirmation',
        validation: {
            reservationHolderNameRequired: 'Please enter hotel reservation name',
            userNameRequired: 'Please enter applicant name',
            relationshipWithGuestRequired: 'Please select relationship with guest',
            emailRequired: 'Please enter email address',
            emailInvalid: 'Please enter a valid email address',
            phoneNumberRequired: 'Please enter phone number',
            countryRequired: 'Please enter country',
            hotelRequired: 'Please select a hotel',
            checkInDateRequired: 'Please enter check-in date',
            checkOutDateRequired: 'Please enter check-out date',
            airportRequired: 'Please select airport / reception counter',
            arrivalDateRequired: 'Please enter estimated arrival date',
            arrivalTimeRequired: 'Please enter estimated arrival time',
            pickupDateRequired: 'Please enter drop-off counter arrival date',
            pickupTimeRequired: 'Please enter drop-off counter arrival time',
            luggageQuantityInteger: 'Luggage quantity must be an integer',
            luggageQuantityMin: 'Luggage quantity must be at least 1',
            luggageQuantityRequired: 'Please enter luggage quantity',
            luggagePhotosRequired: 'Please add at least 1 luggage photo',
            luggagePhotosMax: 'Maximum of 3 photos allowed',
            agreeTermsRequired: 'Please agree to the terms of service',
            phoneNumberFormat: 'Please enter a valid country code and phone number (hyphens are optional)',
            checkOutDateAfterCheckIn: 'Check-out date must be after check-in date',
            pickupDateAfterArrival: 'Drop-off counter arrival must be on or after airport arrival',
            checkInDateAfterArrival: 'Check-in date must be on or after the arrival date',
            checkOutDateAfterPickup: 'Check-out date must be on or after the counter drop-off date',
            pickupDateWithinCheckIn: 'Drop-off counter arrival date must be the check-in day or the day before',
            checkInDateWithinPeriod: 'Check-in date must be within the reservable period',
        },
    },
    edit: {
        title: 'Edit Your Booking',
        editButton: 'Edit Booking Information',
        back: '< Back',
        submit: 'Save Changes',
    },
    complete: {
        title: 'Booking Completed<br>(My Page)',
        editButton: 'Edit Booking Information',
        sections: {
            pickupCounter: 'Drop-off Counter Information',
        },
        serviceEnd: {
            title: 'Service Completed',
            thankYouMessage: 'Thank you for using Air Luggage',
            thankYouMessageWithHotel:
                'Thank you for your stay at <strong>{hotelName}</strong> and for using Air Luggage',
            description: 'Your luggage delivery service has been completed.',
            surveySection: {
                title: 'Survey Request',
                description: 'Please help us improve by completing our survey.',
                buttonText: 'Take Survey',
            },
            hotelLpSection: {
                title: 'Your Next Stay',
                description: 'Book your next stay at <strong>{hotelName}</strong> through the official hotel website.',
                buttonText: 'Visit Hotel Website',
            },
        },
        screenshotWarning: '⚠️ Please <strong>take a screenshot</strong> for offline use.',
        qrCode: {
            title: 'QR Luggage Delivery Pass (Airport & Hotel)',
            description:
                'Show this code at the <strong>airport</strong> counter to drop off, and at the <strong>hotel</strong> front desk to pick up.',
            descriptionSub:
                'Your luggage will be delivered to the specified hotel according to the information you provided.',
            cutoffTime: '⚠️ Cut-off time: <strong>{cutoffTime}</strong> (JST, UTC+9)',
            separateNote: 'Note: This is separate from your Flight or Hotel booking.',
            alt: 'QR Luggage Delivery Pass (Airport & Hotel)',
            trademark: 'QR Code is a registered trademark of DENSO WAVE INCORPORATED in Japan and in other countries.',
        },
        share: {
            button: 'Share QR & Info',
            downloadButton: 'Download QR Code',
            textOnlyHint: 'Note: Your device supports text sharing only. QR image will not be attached.',
        },
        airportInfo: {
            arrivalDateTime: 'Arrival/Stay Dates <strong>(JST, GMT+9)</strong>',
            flightNumber: 'Flight Number',
            accessGuideLink: 'How to get to the counter',
        },
        luggageInfo: {
            quantity: 'Number of Bags',
            unit: 'bags',
            deliveryStatus: 'Delivery Status',
        },
        luggagePhoto: {
            title: 'Luggage Photos',
        },
        addressAnnotation: '(Show this to a taxi driver or local staff)',
        hotelInfo: {
            hotelAddress: 'Hotel Address (EN / JP)',
        },
        confirmationItems: {
            sectionTitle: 'Thank you for your booking',
            subtitle:
                'Thank you for choosing Air Luggage. Please review the important notes below before visiting the counter.',
            importantNotes: '【Important: Next Steps & Notes】',
            deliverySchedule: {
                title: '1. Delivery Schedule',
                estimatedArrival: {
                    label: 'Estimated Arrival',
                    description:
                        'Delivery is estimated to arrive at your hotel approximately <strong>{hours} to {hoursUpper} hours</strong> after drop-off.',
                    note: '*Please note: <strong>Arrival times vary depending on traffic and volume. Specific times cannot be guaranteed.</strong>',
                },
                cutoffTime: {
                    label: 'Cut-off Time',
                    description:
                        'Same-day delivery cut-off is {cutoffTime}. Drop-offs after this time will be delivered the <strong>NEXT DAY</strong>.',
                },
            },
            checkDeliveryStatus: {
                title: '2. Check Delivery Status',
                description: 'You can check the current delivery status via your <strong>My Page</strong>.',
                note: '<strong>There is no need to contact the hotel for status updates.</strong>',
            },
            inCaseOfDamage: {
                title: '3. In Case of Damage',
                description:
                    'If your luggage is damaged, please contact Air Luggage on <a href="{whatsappUrl}" target="_blank" rel="noopener">WhatsApp</a> or <a href="{lineUrl}" target="_blank" rel="noopener">LINE</a> <strong>within 24 hours</strong> of receiving it.',
                note: 'Claims after this period will NOT be accepted.',
            },
        },
    },
    confirmation: {
        title: 'Delivery Confirmation',
        sections: {
            customerInfo: 'Customer Information',
            confirmationItems: 'Confirmation Items',
        },
        customerInfo: {
            reservationHolderName: 'Hotel Reservation Name',
            userName: 'Applicant Name',
        },
        airportInfo: {
            airportName: 'Airport Name',
            airportAddress: 'Airport Address',
            counterName: 'Counter Name',
            counterAddress: 'Counter Address',
        },
        luggageInfo: {
            quantity: 'Number of Bags',
            unit: 'bags',
        },
        luggagePhoto: {
            label: 'Luggage Photo',
        },
        confirmationItems: {
            ...confirmationItemsEn,
            agreeAll: 'I agree to all',
        },
        submit: 'CONFIRM & BOOK',
        back: '< Edit Details',
        error: 'Failed to submit application. Please try again.',
        duplicateApplication: 'An application with the same email, hotel, and dates already exists.',
    },
    relationshipWithGuest: {
        self: 'Self',
        family: 'Family',
        friend: 'Friend',
        colleague: 'Colleague',
        other: 'Other',
    },
    deliveryStatus: {
        bookingConfirmed: 'Booking Confirmed (Awaiting Drop-off)',
        bagsReceivedAtCounter: 'Bags Received at Counter',
        inTransit: 'In Transit',
        arrivedAtHotel: 'Arrived at Hotel',
        pickedUp: 'Picked Up',
        cancelled: 'Canceled',
        delayed: 'Delayed / Action Required',
    },
    serverError: {
        title: 'Under Maintenance',
        heading: 'We are currently under maintenance',
        backToApplication: 'Back to application',
        fallbackNotice: 'Please access again using the QR code provided by your hotel.',
    },
    contact: {
        openButton: 'Contact us',
        close: 'Close',
        title: 'Questions about Air Luggage (luggage delivery)',
        note: "We can't answer questions about hotel reservations (dates, rates, cancellation). Please contact your hotel.",
        whatsapp: 'Chat on WhatsApp',
        line: 'Message us on LINE',
        faq: 'Read the FAQ',
        replyHours:
            "Reply hours: 9:00–18:00 (Japan time). For picking up your bags after 6:00 PM, please ask your hotel's front desk.",
    },
} satisfies typeof ja;

export default en;
